import React, { useEffect, useState } from 'react';
import { Flex, Tree, Typography, Tag, Tooltip } from 'antd';
import styled from 'styled-components';
import {
    DownOutlined,
    LoadingOutlined,
    BoldOutlined,
    EditOutlined,
    DeleteOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import type { TreeProps } from 'antd';
import { DataNode } from 'antd/es/tree';

import { ConfirmationDialog } from '../../../../../shared/ui/ConfirmationDialog';
import { CategoryPositionProps } from '../../products/service/products.interface';
import { botName, webAppName } from '../../../../../utils/common/botInfo';
import { useTheme } from '../../../../../hooks/useTheme';

interface CategoryTreeNode extends DataNode {
    id: number;
    name: string;
    parent_id: number;
    childCategoryList: CategoryTreeNode[];
}

interface CategoriesTreeListProps {
    readonly categoryItems: CategoryTreeNode[];
    readonly onUpdatePosition: (item: CategoryPositionProps) => void;
    readonly onSelectCategory: (categoryList: number[]) => void;
    readonly onDeleteCategory: () => void;
    readonly onRenameCategory: (category: CategoryTreeNode) => void;
    readonly onHighlight: (item: CategoryPositionProps) => void;
    readonly updatingCategoryId: number;
    readonly isListUpdating: boolean;
    readonly selectedCategoryId: number;
}

export const CategoriesTreeList = (props: CategoriesTreeListProps) => {
    const {
        categoryItems,
        onUpdatePosition,
        onDeleteCategory,
        onSelectCategory,
        onRenameCategory,
        onHighlight,
        updatingCategoryId,
        isListUpdating,
        selectedCategoryId,
    } = props;

    const { colorIcon } = useTheme();

    const [items, setItems] = useState<CategoryTreeNode[]>(categoryItems);

    const [title, setTitle] = useState('');

    useEffect(() => {
        if (categoryItems?.length > 0) {
            setItems(categoryItems);
        }
    }, [categoryItems]);

    const onDrop: TreeProps['onDrop'] = info => {
        const draggableItem = info.dragNode;
        onUpdatePosition({
            parent_id: draggableItem.parent_id,
            category_id: draggableItem.id,
            position: info.dropPosition,
        });
    };

    const onCopy = item => {
        const text = `https://t.me/${botName}/${webAppName()}?startapp=${
            item.shop_id
        }_c=${item.id}`;
        navigator.clipboard.writeText(text).then(() => setTitle('Скопировано'));

        setTimeout(() => {
            setTitle('Скопировать');
        }, 2000);
    };

    return (
        <StyledTree>
            <Tree
                style={{
                    width: '100%',
                    position: 'relative',
                }}
                switcherIcon={
                    <StyledDropdown>
                        <DownOutlined />
                    </StyledDropdown>
                }
                onSelect={onSelectCategory}
                selectedKeys={[selectedCategoryId]}
                treeData={items as unknown as CategoryTreeNode[]}
                draggable
                fieldNames={{
                    title: 'name',
                    key: 'id',
                    children: 'childCategoryList',
                }}
                titleRender={category => {
                    return (
                        <StyledCategoryRow>
                            <StyledTitleColumn>
                                <Typography.Text strong={category?.highlight}>
                                    {category.name}{' '}
                                </Typography.Text>
                                {updatingCategoryId === category.id &&
                                    isListUpdating && <LoadingOutlined />}
                                {category.is_slider ? (
                                    <Tag color='purple'>Hа главной</Tag>
                                ) : (
                                    <div></div>
                                )}
                            </StyledTitleColumn>

                            <StyledActionsColumn>
                                <Flex gap={10} align={'center'}>
                                    <Tooltip
                                        destroyTooltipOnHide
                                        title={'Удалить'}
                                    >
                                        <Flex
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                            onClick={event => {
                                                event.stopPropagation();
                                            }}
                                        >
                                            <ConfirmationDialog
                                                activator={
                                                    <DeleteOutlined
                                                        style={{
                                                            fontSize: '20px',
                                                            color: colorIcon,
                                                        }}
                                                    />
                                                }
                                                onConfirm={onDeleteCategory}
                                                title='Удалить?'
                                                description={`Внимание! Товары с категорией "${
                                                    category.name
                                                }" останутся без категории. ${
                                                    category.childCategoryList
                                                        ? 'Удалятся вложенные подкатегории.'
                                                        : ''
                                                }`}
                                                withCheckbox
                                            />
                                        </Flex>
                                    </Tooltip>

                                    <Tooltip
                                        destroyTooltipOnHide
                                        title={'Редактировать'}
                                    >
                                        <Flex
                                            onClick={(
                                                event: React.MouseEvent
                                            ) => {
                                                event.stopPropagation();
                                                onRenameCategory(category);
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <EditOutlined
                                                style={{
                                                    fontSize: '20px',
                                                    color: colorIcon,
                                                }}
                                            />
                                        </Flex>
                                    </Tooltip>

                                    <Tooltip
                                        destroyTooltipOnHide
                                        title={title ? title : 'Скопировать'}
                                    >
                                        <Flex
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                            align={'center'}
                                            onClick={event => {
                                                event.stopPropagation();
                                                onCopy(category);
                                            }}
                                        >
                                            <CopyOutlined
                                                style={{
                                                    fontSize: '20px',
                                                    color: colorIcon,
                                                }}
                                            />
                                        </Flex>
                                    </Tooltip>

                                    <Tooltip
                                        destroyTooltipOnHide
                                        title={
                                            category?.highlight
                                                ? 'Снять выделение'
                                                : 'Выделить жирным'
                                        }
                                    >
                                        <Flex
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                            align={'center'}
                                            onClick={event => {
                                                event.stopPropagation();
                                                onHighlight(category);
                                            }}
                                        >
                                            <BoldOutlined
                                                style={{
                                                    fontSize: '20px',
                                                    color: category?.highlight
                                                        ? 'black'
                                                        : colorIcon,
                                                }}
                                            />
                                        </Flex>
                                    </Tooltip>
                                </Flex>
                            </StyledActionsColumn>
                        </StyledCategoryRow>
                    );
                }}
                onDrop={onDrop}
                allowDrop={({ dropNode, dragNode, dropPosition }) => {
                    return (
                        dropNode.parent_id === dragNode.parent_id &&
                        dropPosition === 1
                    );
                }}
            />
        </StyledTree>
    );
};

const StyledTree = styled.div`
    .ant-tree-treenode-draggable {
        cursor: grab;
        width: 100%;
    }
    .ant-tree-node-content-wrapper {
        flex: 1 1 0;
        width: 100%;
    }
    .ant-motion-collapse {
        width: 100% !important;
    }
    .ant-tree-draggable-icon {
        display: none;
    }
`;

const StyledCategoryRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1 1 0;
    padding: 16px;

    cursor: grab;
`;

const StyledDropdown = styled.div`
    padding: 16px 0;
`;

const StyledTitleColumn = styled.div`
    display: flex;
    flex: 1 1 0;
    justify-content: space-between;
    padding: 5px 0;
`;

const StyledActionsColumn = styled.div``;
